/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

import { z } from "zod";
import {
	DateFilterSchema,
	multiSelectFilterSchema,
} from "./domain-schemas/shared/list-filters.schema";
import {
	WaitlistDesiredWhenSchema,
	WaitlistEntryStatusSchema,
} from "./domain-schemas/waitlist/waitlist.schema";

const CsvSchema = z
	.string()
	.transform((value) => value.split(",").filter(Boolean));

/**
 * Each list facet accepts EITHER the legacy CSV string (`sources=agent,local`
 * ≡ "is any of") or the shared multiselect operator envelope; `desired` is
 * the operator envelope form of the legacy `desiredFrom`/`desiredTo` pair
 * (envelope wins).
 */
export const ListWaitlistEntriesQuerySchema = z.object({
	desired: DateFilterSchema.optional(),
	desiredFrom: z.iso.datetime().optional(),
	desiredTo: z.iso.datetime().optional(),
	eventTypeIds: z
		.union([multiSelectFilterSchema(z.string().max(120)), CsvSchema])
		.optional(),
	limit: z.coerce.number().int().min(1).max(100).default(25),
	offset: z.coerce.number().int().min(0).default(0),
	search: z.string().trim().max(100).optional(),
	sources: z
		.union([
			multiSelectFilterSchema(z.enum(["agent", "local"])),
			CsvSchema.pipe(z.array(z.enum(["agent", "local"]))),
		])
		.optional(),
	statuses: z
		.union([
			multiSelectFilterSchema(WaitlistEntryStatusSchema),
			CsvSchema.pipe(z.array(WaitlistEntryStatusSchema)),
		])
		.optional(),
});

export const WaitlistEntryRowSchema = z.object({
	contact: z.object({
		id: z.string(),
		name: z.string().nullable(),
		nickname: z.string().nullable(),
		phoneE164: z.string().nullable(),
	}),
	createdAt: z.iso.datetime(),
	desiredWhen: WaitlistDesiredWhenSchema,
	eventType: z.object({
		colorBackground: z.string().nullable(),
		id: z.string(),
		label: z.string(),
	}),
	id: z.string().regex(/^\d+$/),
	offeredCount: z.number().int().nonnegative(),
	position: z.number().int().positive().nullable(),
	source: z.enum(["agent", "local"]),
	status: WaitlistEntryStatusSchema,
});

export const WaitlistEntriesResponseSchema = z.object({
	items: z.array(WaitlistEntryRowSchema),
	total: z.number().int().nonnegative(),
});
export type WaitlistEntryRow = z.infer<typeof WaitlistEntryRowSchema>;
export type WaitlistEntriesResponse = z.infer<
	typeof WaitlistEntriesResponseSchema
>;

const TimedSlotSchema = z.object({
	contactId: z.string().optional(),
	eventTypeId: z.string(),
	expiresAt: z.iso.datetime(),
	id: z.string().regex(/^\d+$/),
	slotStart: z.iso.datetime(),
});

export const WaitlistCountsResponseSchema = z.object({
	activeEntries: z.number().int().nonnegative(),
	bookingsThisWeek: z.number().int().nonnegative(),
	openOffers: z.array(TimedSlotSchema),
});
export type WaitlistCountsResponse = z.infer<
	typeof WaitlistCountsResponseSchema
>;

export const JoinWaitlistBodySchema = z
	.object({
		contactId: z.string().min(1),
		desiredUntil: z.iso.datetime(),
		desiredWhen: WaitlistDesiredWhenSchema,
		eventTypeId: z.string().min(1),
		note: z.string().trim().max(1000).nullable().optional(),
	})
	.strict();

export const CancelWaitlistBodySchema = z
	.object({ contactId: z.string().min(1) })
	.strict();

export const WaitlistMutationResponseSchema = z.record(z.string(), z.unknown());

export type JoinWaitlistBody = z.infer<typeof JoinWaitlistBodySchema>;
export type ListWaitlistEntriesQuery = z.infer<
	typeof ListWaitlistEntriesQuerySchema
>;
export type CancelWaitlistBody = z.infer<typeof CancelWaitlistBodySchema>;
